import { combineReducers } from "redux";
import createSagaMiddleware from "redux-saga";
import rootSaga from "./sagas";
import * as Sentry from "@sentry/react";
import routes from "./routes";
import { accessToken } from "./data/access-token/reducer";
import {
  appLinks,
  clientAppSettings,
  clientIds,
  clients,
  inAppPurchaseSettings,
  me,
  newsFeedSettings,
} from "./data/user/reducer";
import { trainers } from "./data/trainers/reducer";
import { assessments } from "./data/assessments/reducer";
import { reducer as toastrReducer } from "react-redux-toastr";
import { loadingBarReducer } from "react-redux-loading-bar";
import {
  DELETE_ACCESS_TOKEN,
  RESET_APP_REQUEST,
} from "./constants/action-types";
import {
  clientExerciseHistory,
  clientProgramIds,
  clientProgramsLoadedSchedule,
  getProgramDayDrillSequence as getPDDSequence,
  orderedProgramListItems,
  orderedProgramLists,
  programDayNotes,
  programDrills,
  programs,
  programsDayDrillOrder,
  workoutTemplates,
} from "./data/program/reducer";
import { enableBatching } from "redux-batched-actions";
import {
  exercises,
  orderedExercises,
  recentExercises,
} from "./data/exercise/reducer";
import { chatState } from "./data/app/reducer";
import appTitleReducer from "./data/app/app-title-slice";
import { skills } from "./data/skill/reducer";
import { requests } from "./data/requests/reducer";
import { registers, savedRegisters } from "./data/register/reducer";
import { account } from "./data/account/reducer";
import { defaults } from "./data/defaults/reducer";
import { groupMembers, groups } from "./data/groups/reducer";
import { triggeredMessages } from "./data/triggered-messaging/reducer";
import { campaigns } from "./data/campaigns/reducer";
import { hasNewStories, stories, storyIndex } from "./data/stories/reducer";
import {
  newNotificationsCount,
  notifications,
} from "./data/notifications/reducer";
import {
  connectedToStripe,
  preClientSignupCheck,
  signupFormOrderedList,
  signupForms,
} from "./data/signup-forms/reducer";
import { pendingClientSignups } from "./data/client-signups/reducer";
import { messages } from "./data/messaging/reducer";
import { pendingBrandedApp } from "./data/pending-branded-app/reducer";
import { leaderBoard } from "./data/leaderBoard/reducer";
import { scheduledMessages } from "./data/scheduled-messages/reducer";
import { notes } from "./data/notes/reducer";
import {
  subscription,
  subscriptionUpdatePreview,
} from "./data/subscription/reducer";
import { geo } from "./data/geo/reducer";
import { configureStore } from "@reduxjs/toolkit";
import { persistStore, persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import rtkApi from "./data/rtkApi";

// export const history = createBrowserHistory({
//   initialEntries: routes,
//   basename: "/web",
// });

const appReducer = combineReducers({
  loadingBar: loadingBarReducer,
  toastr: toastrReducer,
  accessToken: accessToken,
  clients: clients,
  trainers: trainers,
  clientIds: clientIds,
  me: me,
  programs: programs,
  programDrills: programDrills,
  programsDayDrillOrder: programsDayDrillOrder,
  programDayNotes: programDayNotes,
  exercises: exercises,
  recentExercises: recentExercises,
  orderedExercises: orderedExercises,
  workoutTemplates: workoutTemplates,
  appTitle: appTitleReducer,
  chatState: chatState,
  skills: skills,
  requests: requests,
  clientProgramIds: clientProgramIds,
  clientProgramsLoadedSchedule: clientProgramsLoadedSchedule,
  registers: registers,
  savedRegisters: savedRegisters,
  orderedProgramLists: orderedProgramLists,
  orderedProgramListItems: orderedProgramListItems,
  account: account,
  defaults: defaults,
  groups: groups,
  groupMembers: groupMembers,
  triggeredMessages: triggeredMessages,
  campaigns: campaigns,
  stories: stories,
  storyIndex: storyIndex,
  hasNewStories: hasNewStories,
  newNotificationsCount: newNotificationsCount,
  notifications: notifications,
  signupForms: signupForms,
  signupFormOrderedList: signupFormOrderedList,
  pendingClientSignups: pendingClientSignups,
  assessments: assessments,
  messages: messages,
  pendingBrandedApp: pendingBrandedApp,
  leaderBoard: leaderBoard,
  scheduledMessages: scheduledMessages,
  notes: notes,
  connectedToStripe,
  preClientSignupCheck,
  subscription,
  subscriptionUpdatePreview,
  clientExerciseHistory: clientExerciseHistory,
  appLinks: appLinks,
  clientAppSettings: clientAppSettings,
  newsFeedSettings: newsFeedSettings,
  inAppPurchaseSettings: inAppPurchaseSettings,
  geo: geo,
  [rtkApi.reducerPath]: rtkApi.reducer,
});
const rootReducer = (state, action) => {
  if (
    action.type === DELETE_ACCESS_TOKEN.SUCCESS ||
    action.type === RESET_APP_REQUEST
  ) {
    const { loadingBar, toastr, readNotifications } = state;
    state = {
      loadingBar,
      toastr,
      readNotifications,
    };
  }
  return appReducer(state, action);
};
const sagaMiddleware = createSagaMiddleware({
  //   onError: error => RavenJS.captureException(error)
});

const persistConfig = {
  key: "root",
  storage,
  whitelist: ["skills", "defaults", "savedRegisters"],
};

const persistedReducer = persistReducer(
  persistConfig,
  enableBatching(rootReducer),
);

const sentryReduxEnhancer = Sentry.createReduxEnhancer();

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) => {
    return getDefaultMiddleware({
      serializableCheck: false,
    }).concat(rtkApi.middleware, sagaMiddleware);
  },
  enhancers: [sentryReduxEnhancer],
});

sagaMiddleware.run(rootSaga);

export const getProgramDayDrillSequence = (state, programId, day, date) => {
  return getPDDSequence(state.programsDayDrillOrder, programId, day, date);
};

export default store;
export const persistor = persistStore(store);
